<template>
  <div class="sp-hero" :class="classModifiers">
    <div ref="slidesContainer" class="sp-hero__slides">
      <slot />
    </div>

    <sp-hero-slideshow-navigation
      v-if="showNavigation"
      :slides.prop="slides"
      :autoplay.prop="autoplay"
      :current.prop="currentSlideIndex"
      :interval.prop="interval"
      @update-current="handleUpdateCurrent"
    ></sp-hero-slideshow-navigation>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { toBoolean } from "../../utils/props";
import { useSlideshow } from "./composables/slideshow";

const props = defineProps({
  autoplay: {
    type: [Boolean, String],
    default: false,
  },
  interval: {
    type: [Number, String],
    required: false,
    default: undefined,
  },
  size: {
    type: String,
    validator: (value) => ["small", "medium", "large"].includes(value),
    default: "medium",
  },
  fullbleed: {
    type: [Boolean, String],
    default: false,
  },
  rounded: {
    type: [Boolean, String],
    default: false,
  },
});

const { isSmScreen } = useBreakpoints();
const slidesContainer = ref(null);
const { slides, currentSlideIndex, showNavigation, goToSlide } = useSlideshow(slidesContainer);

function handleUpdateCurrent({ detail }) {
  const [index] = detail;
  currentSlideIndex.value = index;
  goToSlide(index);
}

const classModifiers = computed(() => ({
  "--full-bleed": toBoolean(props.fullbleed),
  "--is-small-screen": isSmScreen.value,
  "--rounded": toBoolean(props.rounded),
  [`--size-${props.size}`]: props.size,
}));
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-hero {
  --height: var(--sp-comp-hero-height, auto);
  --min-height: var(--sp-comp-hero-min-height, auto);
  --sp-comp-hero-item-padding-block: calc(var(--height) * 0.095);
  --sp-comp-hero-slideshow-navigation-padding-bottom: calc(var(--height) * 0.095);

  position: relative;
  min-height: var(--min-height);
  height: var(--height);
  width: 100%;

  &.--size-small {
    --height: var(--sp-comp-hero-size-small-height, 21.25rem); // 340px

    &.--is-small-screen {
      --height: var(--sp-comp-hero-size-small-height-sm-screen, 26rem); // 416px
    }
  }

  &.--size-medium {
    --height: var(--sp-comp-hero-size-medium-height, 33.75rem); // 540px

    &.--is-small-screen {
      --height: var(--sp-comp-hero-size-medium-height-sm-screen, 33.75rem); // 540px
    }
  }

  &.--size-large {
    --height: var(--sp-comp-hero-size-large-height, 50rem); // 800px

    &.--is-small-screen {
      --height: var(--sp-comp-hero-size-large-height-sm-screen, 43.75rem); // 700px
    }
  }

  &.--full-bleed {
    ::slotted(sp-hero-item) {
      --sp-comp-hero-item-border-radius: 0;
      --sp-comp-hero-item-background-width: 100vw;
      --sp-comp-hero-item-background-margin-inline: calc(var(--sp-comp-hero-item-background-width) / 2 * -1 + 50%);
      --sp-comp-hero-item-padding-inline: 0;
      --sp-comp-hero-item-padding-inline-sm: 0;
    }
  }

  &.--rounded {
    ::slotted(sp-hero-item) {
      --sp-comp-hero-item-border-radius: var(--sp-sys-border-radius);
    }
  }
}

.sp-hero__slides {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1;
}
</style>
